<template>
  <div class="flex gap-[10px] flex-col">
    <TagInputWithDropdownList
      v-show="hasDigitalAccess"
      ref="digitalInput"
      :label="$t('appliedFilters.digital')"
      :empty-label="$t('appliedFilters.selectCategories')"
      :list="digitalCategories"
      :selected-tags="digitalCatTagList"
      data-text="digitalMedia"
      @add="(tag: ListItem) => setDigitalCategories(tag)"
      @remove="(id: number) => removeDigitalCategoryTag(id)"
    />
    <TagInputWithDropdownList
      v-show="hasPrintAccess"
      ref="printInput"
      :label="$t('appliedFilters.print')"
      :empty-label="$t('appliedFilters.selectCategories')"
      :list="printCategories"
      :selected-tags="printCatTagList"
      data-text="printMedia"
      @add="(tag: ListItem) => setPrintCategories(tag)"
      @remove="(id: number) => removePrintCategoryTag(id)"
    />
    <div v-show="hasPodcastAccess">
      <span class="font-body-2">{{ $t("appliedFilters.podcast") }}</span>
      <div class="my-3 mx-4">
        <template
          v-for="(genre, index) in podcastGenres.categories"
          :key="index"
        >
          <div
            :data-intercom-target="'Podcast media toggle ' + genre.label"
            class="flex justify-between items-center"
          >
            <div class="flex gap-x-2 items-center">
              <OnclusiveCheckbox
                :checked="bifurcatedPodcastGenres[genre.value].isAllSelected"
                @checked="() => handleCheckGenre(genre.value)"
              />
              <div class="flex items-center">
                <span class="font-body-2">
                  {{ genre.label }}
                </span>
              </div>
            </div>
            <button
              class="px-2"
              @click="
                () =>
                  expandedPodcastGenres.includes(genre.value)
                    ? (expandedPodcastGenres = expandedPodcastGenres.filter(
                        (g) => g !== genre.value,
                      ))
                    : expandedPodcastGenres.push(genre.value)
              "
            >
              <font-awesome-icon
                :icon="[
                  'fa-solid',
                  expandedPodcastGenres.includes(genre.value)
                    ? 'chevron-up'
                    : 'chevron-down',
                ]"
                class="w-[14px] h-[14px] text-gray-600 hover:text-gray-800"
              />
            </button>
          </div>
          <div
            v-show="expandedPodcastGenres.includes(genre.value)"
            class="ml-5 my-4 space-y-3"
          >
            <div
              v-for="(subGenre, subIndex) in genre.sub_categories"
              :key="subIndex"
              class="flex items-center justify-between"
              :data-intercom-target="
                'Podcast sub-genre toggle ' + subGenre.label
              "
            >
              <span class="font-body-2 text-slate-500">
                {{ subGenre.label }}
              </span>
              <ToggleSwitch
                :switch-value="selectedPodcastGenres.includes(subGenre.value)"
                @switch-clicked="() => handleCheckSubGenre(subGenre.value)"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
    <div v-show="hasSocialAccess">
      <span class="font-body-2">{{ $t("appliedFilters.social") }}</span>
      <div class="my-3 mx-4">
        <div
          data-intercom-target="Twitter media toggle"
          class="flex justify-between items-center"
        >
          <div class="flex gap-x-2 items-center">
            <OnclusiveCheckbox
              :checked="
                searchFilterStore.getCurrentSearchFilter.twitter_media_type
              "
              @checked="(value) => onSocialCheckboxChecked(value, 'twitter')"
            />
            <div class="w-[18px] h-[18px] flex items-center justify-center">
              <font-awesome-icon
                class="icon"
                :icon="['fab', 'x-twitter']"
                style="width: 18px; height: 18px"
              />
            </div>
            <div class="flex items-center">
              <span class="font-body-2">{{
                $t("appliedFilters.xTwitter")
              }}</span>
            </div>
          </div>
          <button
            v-show="searchFilterStore.getCurrentSearchFilter.twitter_media_type"
            class="px-2"
            @click="toggleTwitterOptions"
          >
            <font-awesome-icon
              :icon="[
                'fa-solid',
                isTwitterOptionsExpanded ? 'chevron-up' : 'chevron-down',
              ]"
              class="w-[14px] h-[14px] text-gray-600 hover:text-gray-800"
            />
          </button>
        </div>
        <div
          v-show="
            isTwitterOptionsExpanded &&
            searchFilterStore.getCurrentSearchFilter.twitter_media_type
          "
          class="ml-5 my-4 space-y-3"
        >
          <div
            class="flex items-center justify-between"
            data-intercom-target="Twitter verified account toggle"
          >
            <span class="font-body-2 text-slate-500">{{
              $t("appliedFilters.verifiedAccount")
            }}</span>
            <ToggleSwitch
              :switch-value="
                searchFilterStore.getCurrentSearchFilter.verified_accounts_only
              "
              @switch-clicked="onSocialToggleClicked('verifiedAccount')"
            />
          </div>
          <div
            class="flex items-center justify-between"
            data-intercom-target="Twitter hide reposts toggle"
          >
            <span class="font-body-2 text-slate-500">{{
              $t("appliedFilters.hideReposts")
            }}</span>
            <ToggleSwitch
              :switch-value="
                searchFilterStore.getCurrentSearchFilter.hide_repost
              "
              @switch-clicked="onSocialToggleClicked('hideReposts')"
            />
          </div>
        </div>
      </div>

      <div
        class="my-3 mx-4"
        :class="[
          hasNoFacebookAccountConnected &&
            'pointer-events-none opacity-50 cursor-not-allowed',
        ]"
      >
        <div
          data-intercom-target="Facebook media toggle"
          class="flex justify-between items-center"
        >
          <div class="flex gap-x-2 items-center">
            <OnclusiveCheckbox
              :checked="
                searchFilterStore.getCurrentSearchFilter.facebook_media_type &&
                !hasNoFacebookAccountConnected
              "
              @checked="(value) => onSocialCheckboxChecked(value, 'facebook')"
            />
            <div class="w-[18px] h-[18px] flex items-center justify-center">
              <font-awesome-icon
                class="icon"
                :icon="['fab', 'facebook']"
                style="width: 18px; height: 18px"
              />
            </div>
            <div class="flex items-center">
              <span class="font-body-2">{{
                $t("appliedFilters.facebook")
              }}</span>
            </div>
          </div>
          <button
            v-show="
              searchFilterStore.getCurrentSearchFilter.facebook_media_type
            "
            class="px-2"
            @click="toggleFacebookOptions"
          >
            <font-awesome-icon
              :icon="[
                'fa-solid',
                isFacebookOptionsExpanded ? 'chevron-up' : 'chevron-down',
              ]"
              class="w-[14px] h-[14px] text-gray-600 hover:text-gray-800"
            />
          </button>
        </div>
        <div
          v-show="
            isFacebookOptionsExpanded &&
            searchFilterStore.getCurrentSearchFilter.facebook_media_type
          "
          class="ml-5 my-4"
        >
          <div
            class="flex items-center justify-between"
            data-intercom-target="Facebook include comments toggle"
          >
            <span class="font-body-2 text-slate-500">{{
              $t("appliedFilters.includeComments")
            }}</span>
            <ToggleSwitch
              :switch-value="
                searchFilterStore.getCurrentSearchFilter.include_comments
              "
              @switch-clicked="onSocialToggleClicked('includeComments')"
            />
          </div>
        </div>
      </div>

      <div
        data-intercom-target="Instagram media toggle"
        class="flex gap-x-2 items-center my-3 mx-4"
        :class="[
          hasNoFacebookAccountConnected &&
            'pointer-events-none opacity-50 cursor-not-allowed',
        ]"
      >
        <OnclusiveCheckbox
          :checked="
            searchFilterStore.getCurrentSearchFilter.instagram_media_type &&
            !hasNoFacebookAccountConnected
          "
          @checked="(value) => onSocialCheckboxChecked(value, 'instagram')"
        />
        <div class="w-[18px] h-[18px] flex items-center justify-center">
          <font-awesome-icon
            class="icon"
            :icon="['fab', 'instagram']"
            style="width: 18px; height: 18px"
          />
        </div>
        <div class="flex items-center">
          <span class="font-body-2">{{ $t("appliedFilters.instagram") }}</span>
        </div>
      </div>

      <div
        data-intercom-target="YouTube media toggle"
        class="flex gap-x-2 items-center my-3 mx-4"
      >
        <OnclusiveCheckbox
          :checked="searchFilterStore.getCurrentSearchFilter.youtube_media_type"
          @checked="(value) => onSocialCheckboxChecked(value, 'youtube')"
        />
        <div class="w-[18px] h-[18px] flex items-center justify-center">
          <font-awesome-icon
            class="icon"
            :icon="['fab', 'youtube']"
            style="width: 18px; height: 18px"
          />
        </div>
        <div class="flex items-center">
          <span class="font-body-2">{{ $t("appliedFilters.youtube") }}</span>
        </div>
      </div>
    </div>
    <span
      v-show="
        !hasPrintAccess &&
        !hasDigitalAccess &&
        !hasPodcastAccess &&
        !hasSocialAccess
      "
      class="text-center py-4"
    >
      {{ $t("appliedFilters.noMediaCategoryAvailable") }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, Ref, ref, watch, onBeforeUnmount, onMounted } from "vue";
import { useNuxtApp } from "nuxt/app";
import { storeToRefs } from "pinia";
import ToggleSwitch from "~/components/shared/toggle-switch/ToggleSwitch.vue";
import OnclusiveCheckbox from "~/components/shared/onclusive-checkbox/OnclusiveCheckbox.vue";
import { useSearchTask } from "~/composables/useSearchTask";
import TagInputWithDropdownList from "~/components/shared/TagInputWithDropdownList.vue";
import { useOrgStore } from "@/store/org";
import { useSearchFiltersStore } from "@/store/searchFilter";
import { isIdPresent } from "@/utils/IsIdPresent";
import { removeObjectById } from "@/utils/RemoveObjectById";
import ListItem from "@/types/ListItemType";
import Emitter from "~/types/Emitter";
import { useSocialStore } from "@/store/social";
import podcastGenres from "@/constants/podcast-genres.json";

const socialStore = useSocialStore();
const orgStore = useOrgStore();
const searchFilterStore = useSearchFiltersStore();

const emit = defineEmits(["performUpdate"]);

const { hasNoFacebookAccountConnected } = storeToRefs(socialStore);
const { $bus } = useNuxtApp() as unknown as { $bus: Emitter };
const digitalCatTagList: Ref<Array<ListItem>> = ref([]);
const printCatTagList: Ref<Array<ListItem>> = ref([]);
const digitalInput: Ref = ref(null);
const printInput: Ref = ref(null);
const isTwitterOptionsExpanded: Ref<boolean> = ref(true);
const isFacebookOptionsExpanded: Ref<boolean> = ref(true);
const selectedPodcastGenres = ref<Array<string>>(
  searchFilterStore.getCurrentSearchFilter.podcast_categories,
);
const bifurcatedPodcastGenres = computed<
  Record<string, { isAllSelected: boolean; selectedSubGenres: Array<string> }>
>(() => {
  return podcastGenres.categories.reduce(
    (
      acc: Record<
        string,
        { isAllSelected: boolean; selectedSubGenres: Array<string> }
      >,
      genre: any,
    ) => {
      const subGenres = genre.sub_categories.map((sub: any) => sub.value);
      const selectedSubGenres = selectedPodcastGenres.value.filter((sg) =>
        subGenres.includes(sg),
      );
      acc[genre.value] = {
        isAllSelected: subGenres.length === selectedSubGenres.length,
        selectedSubGenres,
      };
      return acc;
    },
    {},
  );
});
const expandedPodcastGenres = ref<Array<string>>(
  Object.entries(bifurcatedPodcastGenres.value).reduce((acc, [key, value]) => {
    if (value.selectedSubGenres.length > 0) {
      acc.push(key);
    }
    return acc;
  }, [] as string[]),
);

$bus.$on("CustomDrawer:click", (e: Array<any>) => {
  if (e[1]) {
    digitalInput.value?.collapse();
    printInput.value?.collapse();
    return;
  }
  if (
    ![digitalInput, printInput].some((r) => r.value?.$el.contains(e[0].target))
  ) {
    digitalInput.value?.collapse();
    printInput.value?.collapse();
  }
});

const setDigitalCategories = (tag: any) => {
  if (searchFilterStore?.currentSearchFilter?.web_category) {
    digitalCatTagList.value.push(tag);
    updateDigitalCategories();
  }
};

const setPrintCategories = (tag: any) => {
  if (searchFilterStore?.currentSearchFilter?.print_category) {
    printCatTagList.value.push(tag);
    updatePrintCategories();
  }
};

const onSocialCheckboxChecked = (value: boolean, type: string) => {
  switch (type) {
    case "facebook":
      searchFilterStore.getCurrentSearchFilter.facebook_media_type = value;
      if (!value) {
        searchFilterStore.getCurrentSearchFilter.include_comments = false;
      }
      break;
    case "instagram":
      searchFilterStore.getCurrentSearchFilter.instagram_media_type = value;
      break;
    case "twitter":
      searchFilterStore.getCurrentSearchFilter.twitter_media_type = value;
      if (!value) {
        searchFilterStore.getCurrentSearchFilter.verified_accounts_only = false;
        searchFilterStore.getCurrentSearchFilter.hide_repost = false;
      }
      break;
    case "youtube":
      searchFilterStore.getCurrentSearchFilter.youtube_media_type = value;
      break;
    default:
      break;
  }
  emit("performUpdate");
};

const onSocialToggleClicked = (type: string) => {
  switch (type) {
    case "verifiedAccount":
      searchFilterStore.getCurrentSearchFilter.verified_accounts_only =
        !searchFilterStore.getCurrentSearchFilter.verified_accounts_only;
      break;
    case "hideReposts":
      searchFilterStore.getCurrentSearchFilter.hide_repost =
        !searchFilterStore.getCurrentSearchFilter.hide_repost;
      break;
    case "includeComments":
      searchFilterStore.getCurrentSearchFilter.include_comments =
        !searchFilterStore.getCurrentSearchFilter.include_comments;
      break;
    default:
      break;
  }
  emit("performUpdate");
};

const toggleTwitterOptions = () => {
  isTwitterOptionsExpanded.value = !isTwitterOptionsExpanded.value;
};

const toggleFacebookOptions = () => {
  isFacebookOptionsExpanded.value = !isFacebookOptionsExpanded.value;
};

const updatePrintCategories = () => {
  if (searchFilterStore?.currentSearchFilter?.print_category) {
    searchFilterStore.currentSearchFilter.print_category =
      printCatTagList.value.map((cat) => cat.value ?? "");
    useSearchTask();
  }
};

const updateDigitalCategories = () => {
  if (searchFilterStore?.currentSearchFilter?.web_category) {
    searchFilterStore.currentSearchFilter.web_category =
      digitalCatTagList.value.map((cat) => cat.value ?? "");
    useSearchTask();
  }
};

const removeDigitalCategoryTag = (id: Number) => {
  const catToRemove = digitalCategories.value.find((cat) => cat.id === id);
  if (searchFilterStore?.currentSearchFilter?.web_category) {
    searchFilterStore.currentSearchFilter.web_category.filter(
      (cat: string) => cat !== catToRemove?.value,
    );

    digitalCatTagList.value = removeObjectById(digitalCatTagList.value, id);
    updateDigitalCategories();
  }
};

const removePrintCategoryTag = (id: Number) => {
  const catToRemove = printCategories.value.find((cat) => cat.id === id);
  if (searchFilterStore?.currentSearchFilter?.print_category) {
    searchFilterStore.currentSearchFilter.print_category.filter(
      (cat: string) => cat !== catToRemove?.value,
    );

    printCatTagList.value = removeObjectById(printCatTagList.value, id);
    updatePrintCategories();
  }
};

const resetTags = () => {
  if (searchFilterStore?.currentSearchFilter?.id) {
    const matchingFilter = searchFilterStore.searchFilters.get(
      searchFilterStore.currentSearchFilter.id,
    );

    printCatTagList.value = printCategories.value.filter((category) =>
      (matchingFilter?.print_category ?? []).includes(category.value ?? ""),
    );

    digitalCatTagList.value = digitalCategories.value.filter((category) =>
      (matchingFilter?.web_category ?? []).includes(category.value ?? ""),
    );
  } else {
    printCatTagList.value = [];
    digitalCatTagList.value = [];
  }
};

// checking if access is provided
const hasDigitalAccess: Ref<boolean> = computed(() => {
  return searchFilterStore.getCurrentSearchFilter.web_media_type;
});

const hasPrintAccess: Ref<boolean> = computed(() => {
  return (
    (searchFilterStore.getCurrentSearchFilter.print_media_type &&
      orgStore.hasPrintMediaAccess) ??
    false
  );
});

const hasSocialAccess: Ref<boolean> = computed(() => {
  return (
    (searchFilterStore.getCurrentSearchFilter.social_media_type &&
      orgStore.hasSocialMediaAccess) ??
    false
  );
});

const digitalCategories: Ref<Array<ListItem>> = computed(() => {
  return [
    { id: 1, name: "categories.blog", value: "Blog" },
    { id: 2, name: "categories.generalWeb", value: "General Web" },
    { id: 3, name: "categories.tradeWeb", value: "Trade Web" },
    { id: 4, name: "categories.regionalWeb", value: "Regional Web" },
  ];
});

const printCategories: Ref<Array<ListItem>> = computed(() => {
  return [
    {
      id: 1,
      name: "categories.nationalDaily",
      value: "National Daily",
    },
    {
      id: 2,
      name: "categories.nationalWeekly",
      value: "National Weekly",
    },
    {
      id: 3,
      name: "categories.regionalDaily",
      value: "Regional Daily",
    },
    {
      id: 4,
      name: "categories.regionalWeekly",
      value: "Regional Weekly",
    },
    {
      id: 5,
      name: "categories.regionalPress",
      value: "Regional Press",
    },
    {
      id: 6,
      name: "categories.consumerMagazine",
      value: "Consumer Magazine",
    },
    {
      id: 7,
      name: "categories.tradeMagazine",
      value: "Trade Magazine",
    },
    { id: 8, name: "categories.tabloids", value: "Tabloids" },
    { id: 9, name: "categories.agencies", value: "Agencies" },
    { id: 10, name: "categories.freePress", value: "Free Press" },
  ];
});

const hasPodcastAccess: Ref<boolean> = computed(() => {
  return (
    (searchFilterStore.getCurrentSearchFilter.podcast_media_type &&
      orgStore.hasPodcastAccess) ??
    false
  );
});

const setPrintAndDigitalCategories = () => {
  digitalCatTagList.value = [];
  printCatTagList.value = [];
  if (searchFilterStore?.getCurrentSearchFilter?.web_category) {
    searchFilterStore.getCurrentSearchFilter.web_category.forEach(
      (value: String) => {
        const tag = digitalCategories.value.find(
          (obj: ListItem) => obj.value === value,
        );
        if (tag && !isIdPresent(digitalCatTagList.value, tag.id)) {
          digitalCatTagList.value.push(tag);
        }
      },
    );
  }

  if (searchFilterStore?.getCurrentSearchFilter?.print_category) {
    searchFilterStore.getCurrentSearchFilter.print_category.forEach(
      (value: String) => {
        const tag = printCategories.value.find(
          (obj: ListItem) => obj.value === value,
        );
        if (tag && !isIdPresent(printCatTagList.value, tag.id)) {
          printCatTagList.value.push(tag);
        }
      },
    );
  }
};

const updateExpandedPodcastGenres = () => {
  expandedPodcastGenres.value = Object.entries(bifurcatedPodcastGenres.value)
    .filter(([, value]) => value.selectedSubGenres.length > 0)
    .map(([key]) => key);
};

function handleCheckGenre(genre: string) {
  const genreObj = podcastGenres.categories.find((g) => g.value === genre);
  const allSubGenres = genreObj?.sub_categories
    ? genreObj.sub_categories.map((sub: any) => sub.value)
    : [];
  const isGenreSelected = bifurcatedPodcastGenres.value[genre].isAllSelected;
  if (isGenreSelected) {
    selectedPodcastGenres.value = selectedPodcastGenres.value.filter(
      (sg) => !allSubGenres.includes(sg),
    );
  } else {
    selectedPodcastGenres.value = [
      ...allSubGenres,
      ...selectedPodcastGenres.value.filter((sg) => !allSubGenres.includes(sg)),
    ];
  }
  if (isGenreSelected) {
    expandedPodcastGenres.value = expandedPodcastGenres.value.filter(
      (g) => g !== genre,
    );
  } else {
    expandedPodcastGenres.value = [...expandedPodcastGenres.value, genre];
  }
}
function handleCheckSubGenre(subGenre: string) {
  if (selectedPodcastGenres.value.includes(subGenre)) {
    selectedPodcastGenres.value = selectedPodcastGenres.value.filter(
      (sg) => sg !== subGenre,
    );
  } else {
    selectedPodcastGenres.value = [...selectedPodcastGenres.value, subGenre];
  }
}

watch(
  () => searchFilterStore.searchFilterHash,
  (_newValue) => {
    setPrintAndDigitalCategories();
  },
);

watch(
  () => searchFilterStore.resetSearchFilter,
  (newVal) => {
    if (newVal === true) {
      resetTags();
      searchFilterStore.resetSearchFilter = false;
    }
  },
);

watch(
  () => [hasPrintAccess.value, hasDigitalAccess.value],
  ([newPrintValue, newDigitValue]) => {
    if (newDigitValue === false) {
      digitalCatTagList.value = [];
      if (searchFilterStore.currentSearchFilter) {
        searchFilterStore.currentSearchFilter.web_category = [];
      }
    }

    if (newPrintValue === false) {
      printCatTagList.value = [];
      if (searchFilterStore.currentSearchFilter) {
        searchFilterStore.currentSearchFilter.print_category = [];
      }
    }
  },
);

watch(
  () => searchFilterStore.getCurrentSearchFilter.podcast_categories,
  (newVal) => {
    selectedPodcastGenres.value = [...newVal];
    updateExpandedPodcastGenres();
  },
);

watch(
  () => selectedPodcastGenres.value,
  (newVal) => {
    if (
      newVal.length !==
        searchFilterStore.getCurrentSearchFilter.podcast_categories.length ||
      newVal.some(
        (val) =>
          !searchFilterStore.getCurrentSearchFilter.podcast_categories.includes(
            val,
          ),
      )
    ) {
      searchFilterStore.getCurrentSearchFilter.podcast_categories = newVal;
      emit("performUpdate");
    }
  },
);

onMounted(() => {
  setPrintAndDigitalCategories();
});

onBeforeUnmount(() => {
  $bus.$off("CustomDrawer:click");
});
</script>
